import { formatPrice } from "@/app/price/utils"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { Currency } from "@anaconda/core/price/types"

export interface InsufficientFundsErrorDetails {
  requiredAmount: string
  currentBalance: string
  currency: Currency
  blockchain: Blockchain
  basePrice?: string
  fees?: string
  quantity?: number
}

export class InsufficientFundsError extends Error {
  readonly requiredAmount: string
  readonly currentBalance: string
  readonly currency: Currency
  readonly blockchain: Blockchain
  readonly basePrice?: string
  readonly fees?: string
  readonly quantity?: number

  constructor(details: InsufficientFundsErrorDetails) {
    const message = `Insufficient funds. Required: ${details.requiredAmount} ${details.currency.abbreviation}, Current balance: ${details.currentBalance} ${details.currency.abbreviation}`
    super(message)
    this.name = "InsufficientFundsError"
    this.requiredAmount = details.requiredAmount
    this.currentBalance = details.currentBalance
    this.currency = details.currency
    this.blockchain = details.blockchain
    this.basePrice = details.basePrice
    this.fees = details.fees
    this.quantity = details.quantity

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, InsufficientFundsError)
    }
  }

  get formattedCurrentBalance(): string {
    return formatPrice(Number(this.currentBalance))
  }

  get formattedRequiredAmount(): string {
    return formatPrice(Number(this.requiredAmount))
  }

  get formattedBasePrice(): string | undefined {
    return this.basePrice ? formatPrice(Number(this.basePrice)) : undefined
  }

  get formattedFees(): string | undefined {
    return this.fees ? formatPrice(Number(this.fees)) : undefined
  }
}

export function isInsufficientFundsError(
  error: unknown,
): error is InsufficientFundsError {
  return error instanceof InsufficientFundsError
}

export class NotFoundError extends Error {
  constructor(message = "Resource not found") {
    super(message)
    this.name = "NotFoundError"

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NotFoundError)
    }
  }
}

export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError
}
